import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { MessageCircleWarning, X } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import { useTrayNotices } from '@/shared/components/NoticeTray';
import { pointsToCurrentRoute } from '@/shared/lib/current-route';
import { useCustomerAgentAvailable } from '../hooks/useCustomerAgentAvailable';
import { useWhatsappStatus } from '../hooks/useWhatsapp';
import { CUSTOMER_AGENT_SETTINGS_PATH } from '../lib/connection-copy';
import { isAppReminderDismissed, whatsappNotice, writeAppReminderDismissal } from '../lib/whatsapp-notice';
import styles from './WhatsappStatusBar.module.css';

/**
 * Faixa global da conexão do WhatsApp (change whatsapp-channel, D32), junto
 * da faixa da impressão no `PanelLayout`. Só para quem tem a disponibilidade
 * do Atendente IA com `settings.read`: trial, Essencial e lojas fora do
 * piloto nunca a veem, e nada é lido para elas.
 */
export function WhatsappStatusBar() {
  const { available } = useCustomerAgentAvailable('settings.read');
  if (!available) return null;
  return <WhatsappStatusBarContent />;
}

function WhatsappStatusBarContent() {
  const tenantId = useAuthStore((state) => state.selectedTenant?.id ?? '');
  // Revalidado pela campainha `whatsapp.connection_updated` e na reconexão: a
  // conexão de tempo real é a compartilhada de pedidos, e a faixa a mantém
  // aberta em qualquer tela.
  useOrdersSignalR();
  const { data: status } = useWhatsappStatus();

  // A dispensa desta sessão, à parte do armazenamento: se a escrita falhar, o
  // clique ainda esconde o lembrete por 24 h enquanto a aba estiver aberta.
  const [dismissedAt, setDismissedAt] = useState<Readonly<Record<string, number>>>({});
  const location = useLocation();

  // A leitura do armazenamento acontece no render de propósito: a chave é por
  // loja, então trocar de estabelecimento já reavalia a dispensa.
  const reminderDismissed = isAppReminderDismissed(tenantId, dismissedAt[tenantId]);
  const notice = status ? whatsappNotice(status, { reminderDismissed }) : null;
  // No resumo do topo, a conexão caída ou presa é urgente (as mensagens não chegam); o lembrete dos 14 dias, não.
  useTrayNotices(
    notice
      ? [{ key: `whatsapp:${notice.kind}`, text: notice.summary, level: notice.kind === 'app_reminder' ? 'info' : 'urgent' }]
      : [],
  );
  if (!notice) return null;
  // Na própria aba do Atendente IA, o link levaria a lugar nenhum.
  const showLink = !pointsToCurrentRoute(CUSTOMER_AGENT_SETTINGS_PATH, location);

  const dismiss = () => {
    const at = Date.now();
    writeAppReminderDismissal(tenantId, at);
    setDismissedAt((current) => ({ ...current, [tenantId]: at }));
  };

  if (notice.kind === 'app_reminder') {
    return (
      <div className={styles.reminder} role="status" data-testid="whatsapp-banner" data-kind={notice.kind}>
        <MessageCircleWarning size={14} className={styles.icon} aria-hidden="true" />
        <span className={styles.text}>{notice.text}</span>
        {showLink && (
          <Link to={CUSTOMER_AGENT_SETTINGS_PATH} className={styles.reminderLink}>
            {notice.linkLabel}
          </Link>
        )}
        <button type="button" className={styles.dismiss} onClick={dismiss} aria-label="Lembrar depois">
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    // Não dispensável: some sozinha quando o problema acaba.
    <div className={styles.banner} role="alert" data-testid="whatsapp-banner" data-kind={notice.kind}>
      <MessageCircleWarning size={16} className={styles.icon} aria-hidden="true" />
      <span className={styles.text}>{notice.text}</span>
      {showLink && (
        <Link to={CUSTOMER_AGENT_SETTINGS_PATH} className={styles.link}>
          {notice.linkLabel}
        </Link>
      )}
    </div>
  );
}
